import { type ReactElement, type ReactNode } from 'react'
import cx from 'classnames'
import styles from './ChoiceList.module.css'
import {
  GridList,
  GridListItem,
  type Key,
  type Selection,
} from 'react-aria-components'
import { Icon, type IconName } from '@/components/Icon'

export interface ChoiceListProps<T extends object> {
  mode?: 'single' | 'multi'
  layout?: 'stack' | 'grid'
  children?: ReactNode | ((item: T) => ReactElement)
  items?: T[]
  selectedKeys?: Selection
  disabledKeys?: Iterable<Key>
  disallowEmptySelection?: boolean
  onSelectionChange?: (selection: Selection) => unknown
  'aria-label'?: string
  'aria-labelledby'?: string
  /**
   * Test ID attribute for the grid list.
   */
  testId?: string
}

/**
 * Group of selectable choices, single- or multi-select.
 */
export function ChoiceList<T extends object>({
  children,
  mode = 'single',
  layout = 'stack',
  testId,
  ...rest
}: ChoiceListProps<T>) {
  return (
    <GridList
      className={cx(styles.root, layout === 'grid' && styles.grid)}
      selectionMode={mode === 'single' ? 'single' : 'multiple'}
      selectionBehavior="toggle"
      layout={layout === 'grid' ? 'grid' : 'stack'}
      data-test={testId}
      {...rest}
    >
      {children}
    </GridList>
  )
}

interface ChoiceProps {
  label: string
  description?: ReactNode
  isDisabled?: boolean
  decoration?: ReactNode
  id?: string
  withoutSelectionIcon?: boolean
  /**
   * Test ID attribute for the choice item.
   */
  testId?: string
  onPress?: () => void
}

/**
 * A selectable choice.
 */
export function Choice({
  isDisabled,
  label,
  description,
  decoration,
  id,
  withoutSelectionIcon,
  testId,
  onPress,
}: ChoiceProps) {
  return (
    <GridListItem
      onPress={onPress}
      textValue={label}
      isDisabled={isDisabled}
      id={id}
      data-test={testId}
      className={({
        isSelected,
        isFocusVisible,
        isHovered,
        isPressed,
        isDisabled,
      }) =>
        cx(
          styles.option,
          isFocusVisible && styles.focusVisible,
          isHovered && styles.hover,
          isPressed && styles.pressed,
          isDisabled && styles.disabled,
          isSelected && styles.selected,
        )
      }
    >
      {({ selectionMode, isSelected }) => {
        const iconName: IconName =
          selectionMode === 'single'
            ? isSelected
              ? 'radio-check'
              : 'radio-default'
            : isSelected
              ? 'checkbox-ticked'
              : 'checkbox-default'
        return (
          <>
            {decoration && (
              <div className={styles.decoration}>{decoration}</div>
            )}
            <div className={styles.text}>
              <div className={styles.label}>{label}</div>
              {description && (
                <div className={styles.description}>{description}</div>
              )}
            </div>
            {!withoutSelectionIcon && (
              <div className={styles.icon}>
                <Icon name={iconName} />
              </div>
            )}
          </>
        )
      }}
    </GridListItem>
  )
}

interface IconDecorationProps {
  children: ReactNode
  boxed?: boolean
}

/**
 * Icon decoration for a {@link Choice}. Pass an icon as children; use `boxed`
 * to render it inside a bordered box.
 */
export function IconDecoration({ children, boxed }: IconDecorationProps) {
  return (
    <div className={cx(styles.iconDecoration, boxed && styles.boxed)}>
      {children}
    </div>
  )
}
